import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { catchError, throwError } from 'rxjs';

/**
 * HTTP Interceptor for handling errors globally
 * 
 * This interceptor handles:
 * - 401 Unauthorized: Redirects to Keycloak login
 * - 403 Forbidden: Logs error (user doesn't have permission)
 * - 500+ Server Errors: Logs error
 * - Network Errors: Logs error
 * 
 * @example
 * // In app.config.ts:
 * provideHttpClient(withInterceptors([errorInterceptor]))
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);
  const AUTH_REDIRECT_DEBOUNCE_MS = 5000;

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle different error types
      if (error.error instanceof ErrorEvent) {
        // Client-side or network error
        console.error('Network error occurred:', error.error.message);
      } else {
        // Backend returned an unsuccessful response code
        switch (error.status) {
          case 401:
            // Unauthorized - token expired or invalid
            // Only redirect to login if not already on Keycloak login page and not during initialization
            const currentUrl = window.location.href;
            const isKeycloakUrl = currentUrl.includes('keycloak') || currentUrl.includes('/auth/realms/');
            const isInitializing = sessionStorage.getItem('keycloak-initializing') === 'true';
            const lastRedirectAt = parseInt(sessionStorage.getItem('auth-last-401-redirect-at') || '0', 10);
            const now = Date.now();
            
            if (!isKeycloakUrl && !isInitializing) {
              // Try one silent refresh first; avoid forced logout on transient 401s.
              keycloak.updateToken(0).then(
                () => {
                  console.warn('Received 401 but token refresh succeeded; suppressing forced login redirect.');
                },
                (refreshError) => {
                  const refreshMsg = String(refreshError?.message || refreshError || '').toLowerCase();
                  const isTransient =
                    refreshError?.status === 0 ||
                    refreshMsg.includes('network') ||
                    refreshMsg.includes('failed to fetch') ||
                    refreshMsg.includes('timeout') ||
                    refreshMsg.includes('502') ||
                    refreshMsg.includes('503') ||
                    refreshMsg.includes('504');

                  if (isTransient) {
                    console.warn('401 occurred during transient auth/network issue; skipping immediate login redirect.');
                    return;
                  }

                  if (now - lastRedirectAt > AUTH_REDIRECT_DEBOUNCE_MS) {
                    sessionStorage.setItem('auth-last-401-redirect-at', String(now));
                    console.error('Unauthorized request - redirecting to login');
                    keycloak.login({
                      redirectUri: window.location.origin + window.location.pathname,
                    });
                  } else {
                    console.warn('Skipping repeated 401 login redirect (debounced).');
                  }
                }
              );
            } else if (isKeycloakUrl) {
              console.log('Already on Keycloak page, skipping redirect');
            } else if (isInitializing) {
              console.log('Keycloak is initializing, skipping redirect to prevent loops');
            }
            break;

          case 403:
            // Forbidden - user doesn't have permission
            console.error('Access forbidden:', error.message);
            break;

          case 404:
            // Not found
            console.error('Resource not found:', error.url);
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors
            console.error('Server error occurred:', error.status, error.message);
            break;

          default:
            // Other errors
            console.error(
              `Backend returned code ${error.status}, ` +
                `body was: ${JSON.stringify(error.error)}`
            );
        }
      }

      // Re-throw the error so the calling code can handle it
      return throwError(() => error);
    })
  );
};





